/* ════════════════════════════════════════════════════════════════════════
   Hero modernization — JS
   ────────────────────────────────────────────────────────────────────────
   Load this with `defer` alongside bundle.js in layout/theme.liquid:
     <script src='{{ "hero-modern.js" | asset_url }}' defer='defer'></script>

   Three independent, defensively-guarded modules. Each one no-ops cleanly
   if its target elements aren't present on the page (most pages have no
   slideshow at all, so this script does nothing measurable there).
   ════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  var isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

  /* ── Module 1: Parallax ───────────────────────────────────────────────
     Scroll-driven, rAF-throttled. Targets .hero-parallax-media directly
     — this class is applied to the theme's own .media element (via the
     `class:` param passed into media-with-art-direction.liquid), not a
     wrapper div, so the existing position:absolute/inset:0 chain that
     makes the image fill its container is never disturbed. */
  function initParallax() {
    if (prefersReducedMotion) return;
    var layers = document.querySelectorAll('.hero-parallax-media');
    if (!layers.length) return;

    var ticking = false;

    function update() {
      layers.forEach(function (layer) {
        var container = layer.closest('.image-banner, .split-image-banner');
        var rect = (container || layer).getBoundingClientRect();
        var viewportH = window.innerHeight || document.documentElement.clientHeight;
        // Only compute while the slide is anywhere near the viewport —
        // skip the work entirely for slides scrolled far out of view.
        if (rect.bottom < -200 || rect.top > viewportH + 200) return;
        var center = rect.top + rect.height / 2;
        var offset = (center - viewportH / 2) * 0.06; // 6% drift — subtle by design
        offset = Math.max(-30, Math.min(30, offset));
        layer.style.setProperty('--hero-parallax-y', offset + 'px');
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* ── Module 2: Word-by-word reveal trigger ───────────────────────────
     Adds .is-revealed once the title enters the viewport. Re-triggers on
     re-entry is intentionally NOT done (one-time reveal per page load) —
     a hero title re-animating every time someone scrolls past it reads
     as gimmicky rather than premium. */
  function initWordReveal() {
    var titles = document.querySelectorAll('[data-hero-word-reveal]');
    if (!titles.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      titles.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    titles.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Module 3: Cursor tilt ────────────────────────────────────────────
     Desktop pointer only. Explicitly skipped on coarse pointers (touch)
     since there is no persistent cursor position to tilt toward, and a
     stuck tilt angle on mobile would look broken rather than premium. */
  function initTilt() {
    if (prefersReducedMotion || isCoarsePointer) return;
    var targets = document.querySelectorAll('.hero-tilt-target');
    if (!targets.length) return;

    targets.forEach(function (target) {
      target.classList.add('has-tilt-support');
      var rect = null;
      var maxTilt = 4; // degrees — intentionally subtle

      function onEnter() {
        rect = target.getBoundingClientRect();
      }

      function onMove(e) {
        if (!rect) rect = target.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width; // 0..1
        var py = (e.clientY - rect.top) / rect.height; // 0..1
        var tiltX = (py - 0.5) * -2 * maxTilt;
        var tiltY = (px - 0.5) * 2 * maxTilt;
        target.style.transform =
          'rotateX(' + tiltX.toFixed(2) + 'deg) rotateY(' + tiltY.toFixed(2) + 'deg)';
      }

      function onLeave() {
        target.style.transform = 'rotateX(0deg) rotateY(0deg)';
        rect = null;
      }

      target.addEventListener('pointerenter', onEnter);
      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerleave', onLeave);
    });
  }

  function init() {
    initParallax();
    initWordReveal();
    initTilt();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();